import { useEffect, useState } from 'react';

/**
 * T-VIEW-AS — per-tab "View As Sales Rep" flag (superAdmin impersonation).
 *
 * Stored in **sessionStorage only** — per-tab, NEVER localStorage, NEVER
 * cross-tab propagation, and NEVER the MSAL token cache. A second tab the
 * superAdmin opens stays full admin. `apiFetch` reads `isViewingAsSalesRep()`
 * synchronously on every request to attach the `X-View-As-Role` header; the
 * server is the load-bearing enforcement (downscope + read-only gate).
 *
 * This module is deliberately framework-light + dependency-free (no `apiFetch`
 * import) so `api/client.ts` can import the reader without an import cycle.
 * The enter/exit *actions* live in `./viewAs.ts`.
 */
const STORAGE_KEY = 'rr.viewAs.salesRep';
const CHANGE_EVENT = 'rr:view-as-change';

/** True iff this tab is currently viewing as Sales Rep. Safe in non-DOM envs. */
export function isViewingAsSalesRep(): boolean {
  try {
    return (
      typeof globalThis.sessionStorage !== 'undefined' &&
      globalThis.sessionStorage.getItem(STORAGE_KEY) === '1'
    );
  } catch {
    return false;
  }
}

/** Set/clear the per-tab flag and notify in-tab listeners (banner, nav). */
export function setViewAsSalesRepFlag(on: boolean): void {
  try {
    if (typeof globalThis.sessionStorage !== 'undefined') {
      if (on) globalThis.sessionStorage.setItem(STORAGE_KEY, '1');
      else globalThis.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable (private mode / SSR) — non-fatal.
  }
  try {
    globalThis.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // no DOM (unit tests) — fine.
  }
}

/** React binding: re-renders the subscriber when the per-tab flag changes. */
export function useViewingAsSalesRep(): boolean {
  const [on, setOn] = useState<boolean>(isViewingAsSalesRep);
  useEffect(() => {
    const handler = (): void => setOn(isViewingAsSalesRep());
    globalThis.addEventListener(CHANGE_EVENT, handler);
    return () => globalThis.removeEventListener(CHANGE_EVENT, handler);
  }, []);
  return on;
}
